import NpsNeutral from './visualAssets/NpsNeutral.svg?component';
import NpsPromoter from './visualAssets/NpsPromoter.svg?component';
import NpsDetractor from './visualAssets/NpsDetractor.svg?component';
import NpsEmpty from '@Shared/components/visuals/visualAssets/NpsEmpty.svg?component';

import { roundAwayFromZero } from '@Shared/utils/util';

export enum DeltaType {
  // The 'raw' delta between the current and previous value i.e. current - previous
  PointDelta,
  // The percentage change between the previous and the current i.e. we've increased by 10%
  PercentageDelta,
  // For response count if the previous period is null we count it as 0
  ResponseDelta,

  // The raw delta bwtween the current and previous value in Metric Type: Raw Score Average i.e. 0-10
  RawDelta,
}

// These should match the enum inside the /Portal.Shared.Entities/Dashboard/Entities.cs
// @FIXME: We should not use numeric values for metric type and instead use strings
//         however, for backwards compatibility we need some sort of layer to convert
//         the numeric values to strings as well.  Leaving this for now since it's a
//         very annoying migration with low value.
export enum MetricType {
  Nps = 0,
  Csat = 1,
  TopBox = 2,
  NpsDriver = 3,
  RawScoreAverage = 4,
  Count = 10,
  CountSince = 11,
}

export enum Type {
  Negative = 'Negative',
  Neutral = 'Neutral',
  Positive = 'Positive',
  Null = 'Null',
}

export enum CardIcon {
  FourBoxesWithCheckmark = 'FourBoxesWithCheckmark',
  LoveHeartInSpeechBubble = 'LoveHeartInSpeechBubble',
  ThumbsDownInSpeechBubble = 'ThumbsDownInSpeechBubble',
  CogAroundSpanner = 'CogAroundSpanner',
  PromotorStreak = 'PromotorStreak',
  DetractorStreak = 'DetractorStreak',
  Clock = 'Clock',
  SadFaceWithThumbsDown = 'SadFaceWithThumbsDown',
  HappyFaceWithHeart = 'HappyFaceWithHeart',
  NeutralFaceWithArrows = 'NeutralFaceWithArrows',
  PromoterFaceWithArrow = 'PromoterFaceWithArrow',
  DetractorFaceWithArrows = 'DetractorFaceWithArrows',
}

export function calculateMetricValue(value: number | undefined | null, type?: MetricType, defaultValue = '-'): string {
  // use `-` as null indicator (not 0)
  if (value === null || value === undefined || type === undefined || type === null) {
    // note: count since doesn't get included here.
    if (type == MetricType.Count) return '0';
    return defaultValue;
  }

  switch (type) {
    case MetricType.Csat:
    case MetricType.TopBox:
      // add percentage
      return `${roundAwayFromZero(value * 100)}%`;
    case MetricType.Nps:
      // prefix with sign always
      return `${value > 0 ? '+' : ''}${roundAwayFromZero(value * 100)}`;
    case MetricType.RawScoreAverage:
      return `${value.toFixed(2)}`;
    case MetricType.Count:
      return `${value}`;
    case MetricType.CountSince:
      return `${value}`;
    default:
      return defaultValue;
  }
}

export function getMetricImage(value?: number | null) {
  if (value === null || value === undefined || !Number.isFinite(value)) return NpsEmpty;
  if (value === 0) return NpsNeutral;
  if (value > 0) return NpsPromoter;
  if (value < 0) return NpsDetractor;

  // should be unreachable
  return NpsEmpty;
}

export function calculateDeltaValue(
  obj: { currentValue?: number; previousValue?: number },
  type: DeltaType
): number | undefined {
  if (type == DeltaType.ResponseDelta) {
    return (obj.currentValue ?? 0) - (obj.previousValue ?? 0);
  }

  if (obj.previousValue == null || obj.currentValue == null) return undefined;

  switch (type) {
    case DeltaType.PercentageDelta:
      return obj.previousValue != 0 ? ((obj.currentValue - obj.previousValue) / obj.previousValue) * 100 : undefined;
    case DeltaType.PointDelta:
      return obj.currentValue - obj.previousValue;
  }
}

export function formatDeltaValue(value: number | undefined | null, type: DeltaType): string {
  // use `-` as null indicator (not 0)
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  switch (type) {
    case DeltaType.PercentageDelta:
      // add percentage
      return `${value}%`;
    case DeltaType.PointDelta: {
      // prefix with sign always
      const roundedValue = roundAwayFromZero(value * 100);
      return `${roundedValue > 0 ? '+' : ''}${roundedValue}`;
    }
    case DeltaType.ResponseDelta: {
      const roundedValue = roundAwayFromZero(value);
      return `${roundedValue > 0 ? '+' : ''}${roundedValue}`;
    }
    case DeltaType.RawDelta:
      return `${value.toFixed(2)}`;
  }
}

// Only supports NPS and CSAT, and Count/CountSince
export function getMetricColour(value: number | null | undefined, metricType?: MetricType) {
  if (
    value === null ||
    value === undefined ||
    metricType === null ||
    metricType === undefined ||
    metricType == MetricType.Count ||
    metricType == MetricType.CountSince
  ) {
    return '#4F4F4F';
  } else if (value < 0) {
    return '#D4002B';
  } else if (value === 0) {
    return '#4F4F4F';
  } else if (value > 0) {
    return '#0C8A65';
  }
}

export function generalisedMetricLabel(value: number | string) {
  switch (Number(value)) {
    case -1:
      return 'Negative';
    case 0:
      return 'Neutral';
    case 1:
      return 'Positive';
    default:
      return 'Unknown';
  }
}

export function getImageUrl(localUrl: string) {
  if (window.injectedUrl) {
    return window.injectedUrl + localUrl;
  } else {
    return localUrl;
  }
}

export function getLabel(metricType: MetricType | undefined) {
  if (metricType == MetricType.Nps) {
    return {
      [Type.Positive]: 'Promoter',
      [Type.Neutral]: 'Neutral',
      [Type.Negative]: 'Detractor',
      [Type.Null]: 'Blank',
    } as Record<Type, string>;
  }
  return {
    [Type.Positive]: 'Positive',
    [Type.Neutral]: 'Neutral',
    [Type.Negative]: 'Negative',
    [Type.Null]: 'Blank',
  } as Record<Type, string>;
}

export function lowercaseNoSpaces(label?: string) {
  return label?.replace(/\s+/g, '').toLowerCase();
}
